<template>
    <b-card no-body>
        <b-tabs card>
            <b-tab title="Import Headers" active>
                <b-card-body>
                    <form-wizard
                        shape="tab"
                        color="#3498db"
                        title="Import Orders"
                        subtitle="Follow the steps to import the Orders"
                        @on-complete="onComplete"
                        @on-loading="setLoading"
                    >
                        <tab-content
                            title="Import Headers"
                            :beforeChange="() => !!headerDataList.length"
                        >
                            <simple-csv-import
                                v-model="headerDataList"
                                :startRowNo="orderHeaderSettings.rowno"
                                :columnsMap="headerColumnsMap"
                                placeHolderText="Select Header File Here..."
                                dropPlaceHolderText="Drop Header File Here..."
                            >
                            </simple-csv-import>

                            <p
                                class="mt-4 mb--1"
                                v-show="headerDataList.length"
                            >
                                <b>Total Records:</b>
                                {{
                                    headerDataList.length
                                }}&nbsp;&nbsp;&nbsp;&nbsp;
                                <i v-show="headerDataList.length > 5"
                                    >Only First 5 Rows Shown</i
                                >
                            </p>
                            <b-table
                                :items="headerDataList.slice(0, 5)"
                                responsive
                            >
                            </b-table>
                        </tab-content>
                        <tab-content
                            title="Import Details"
                            :beforeChange="processUploadData"
                        >
                            <simple-csv-import
                                v-model="detailsDataList"
                                :startRowNo="orderDetailsSettings.rowno"
                                :columnsMap="detailsColumnsMap"
                                placeHolderText="Select Details File Here..."
                                dropPlaceHolderText="Drop Header File Here..."
                            >
                            </simple-csv-import>

                            <p
                                class="mt-4 mb--1"
                                v-show="detailsDataList.length"
                            >
                                <b>Total Records:</b>
                                {{
                                    detailsDataList.length
                                }}&nbsp;&nbsp;&nbsp;&nbsp;
                                <i v-show="detailsDataList.length > 5"
                                    >Only First 5 Rows Shown</i
                                >
                            </p>
                            <b-table
                                :items="detailsDataList.slice(0, 5)"
                                responsive
                            ></b-table>
                        </tab-content>
                        <tab-content
                            title="Upload"
                            :before-change="uploadOrders"
                        >
                            <h6>Items to Be Uploaded!!</h6>
                            <b-table :fields="i_fields" :items="i_orders">
                                <template #cell(index)="data">
                                    {{ data.index + 1 }}
                                </template>
                                <template #cell(upload_status)="data">
                                    <b-badge
                                        :id="`upload-order-${data.index}`"
                                        :variant="
                                            data.value | filterStatusVariant
                                        "
                                        >{{ data.value }}</b-badge
                                    >
                                    <!-- Show tooltip if there are errors -->
                                    <b-tooltip
                                        v-if="data.item.error_list.length > 0"
                                        :target="`upload-order-${data.index}`"
                                        triggers="hover"
                                    >
                                        Errors:
                                        <!-- loop through the list of errors and display each -->
                                        <ul
                                            v-for="error_set in data.item
                                                .error_list"
                                            :key="error_set.index"
                                        >
                                            <li
                                                v-for="error in error_set[1]"
                                                :key="`error-${error}`"
                                            >
                                                {{ error }}
                                            </li>
                                        </ul>
                                    </b-tooltip>
                                </template>
                            </b-table>
                            <b-progress
                                class="mt-2"
                                :max="i_orders.length"
                                show-value
                            >
                                <b-progress-bar
                                    :value="completedUploads"
                                    variant="success"
                                ></b-progress-bar>
                                <b-progress-bar
                                    :value="failedUploads"
                                    variant="danger"
                                ></b-progress-bar>
                            </b-progress>
                        </tab-content>
                        <tab-content title="Complete">
                            <h6>Report</h6>
                            <b-list-group>
                                <b-list-group-item
                                    >Total Orders:
                                    {{ i_orders.length }}</b-list-group-item
                                >

                                <b-list-group-item variant="success"
                                    >Successful Uploads:
                                    {{ completedUploads }}</b-list-group-item
                                >
                                <b-list-group-item variant="danger"
                                    >Failed Uploads:
                                    {{ failedUploads }}</b-list-group-item
                                >
                            </b-list-group>
                        </tab-content>
                        <div class="text-center">
                            <b-spinner
                                v-if="uploading"
                                variant="success"
                                label="Spinning"
                            ></b-spinner>
                        </div>
                    </form-wizard>
                </b-card-body>
            </b-tab>

            <b-tab title="Settings">
                <b-card-body>
                    <div class="accordion" role="tablist">
                        <b-card no-body class="mb-1">
                            <b-card-header
                                header-tag="header"
                                class="p-1"
                                role="tab"
                            >
                                <b-button
                                    block
                                    v-b-toggle.header-accordion
                                    variant="info"
                                    >Headers Settings</b-button
                                >
                            </b-card-header>
                            <b-collapse
                                id="header-accordion"
                                visible
                                accordion="settings-accordion"
                                role="tabpanel"
                            >
                                <b-card-body>
                                    <upload-data-settings
                                        key="headers"
                                        @input="
                                            updateDataSettings($event, true)
                                        "
                                        :value="orderHeaderSettings"
                                        :data-fields="orderHeaderFields"
                                    ></upload-data-settings>
                                </b-card-body>
                            </b-collapse>
                        </b-card>

                        <b-card no-body class="mb-1">
                            <b-card-header
                                header-tag="header"
                                class="p-1"
                                role="tab"
                            >
                                <b-button
                                    block
                                    v-b-toggle.details-accordion
                                    variant="info"
                                    >Details Settings</b-button
                                >
                            </b-card-header>
                            <b-collapse
                                id="details-accordion"
                                accordion="settings-accordion"
                                role="tabpanel"
                            >
                                <b-card-body>
                                    <upload-data-settings
                                        key="details"
                                        @input="updateDataSettings($event)"
                                        :value="orderDetailsSettings"
                                        :data-fields="orderDetailsFields"
                                    ></upload-data-settings>
                                </b-card-body>
                            </b-collapse>
                        </b-card>
                    </div>
                </b-card-body>
            </b-tab>
        </b-tabs>
    </b-card>
</template>

<script>
const countryLookup = require('country-code-lookup')
import SimpleCsvImport from '@/components/SimpleCsvImport'

import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'

import UploadDataSettings from '@/components/UploadDataSettings'
import { capitalCase } from 'change-case'
import popupMixins from '@/mixins/popupMixins'

import moment from 'moment'

/**
 * The allowed date formats that can be parsed for the schedule at field
 */
const allowedDateFormats = [
    'DD/MM/YYYY',
    'D/M/YYYY',
    'MM/DD/YYYY',
    'M/D/YYYY',
    'YYYY-MM-DD',
    'YYYY-DD-MM',
    'MM/DD/YY',
    'M/D/YY',
    'D/M/YY',
    'DD/MM/YY',
    'DD-MMM-YY',
    'DD-MMM-YYYY',
    'MMMM DD, YYYY',
]

// Order field names and whether they are required
const orderHeaderFields = [
    { name: 'reference_no', required: true },
    { name: 'customer_code', required: true },
    { name: 'customer_name', required: true },
    { name: 'customer_email', required: false },
    { name: 'customer_phone', required: false },
    { name: 'customer_address1', required: false },
    { name: 'customer_address2', required: false },
    { name: 'customer_city', required: false },
    { name: 'customer_country', required: false },
    { name: 'schedule_at', required: true },
    {
        name: 'schedule_at_date_format',
        required: true,
        options: allowedDateFormats.map(dateFormat => ({
            value: dateFormat,
            text: moment()
                .clone()
                .format(dateFormat),
        })),
    },
    { name: 'order_type', required: true },
    { name: 'total_price', required: true },
]

const orderDetailsFields = [
    { name: 'customer_code', required: true },
    { name: 'reference_no', required: true },
    { name: 'item_code', required: true },
    { name: 'item_name', required: true },
    { name: 'description', required: false },
    { name: 'item_type', required: true },
    { name: 'quantity', required: true },
    { name: 'price', required: true },
]

/**
 * Posible statuses during upload
 */
const statuses = {
    COMPLETE: 'complete',
    PENDING: 'pending',
    INVALID_LOCATION: 'complete: invalid location',
    ERROR: 'error: hover for details',
}

const headerKey = 'orderHeaderUploadSettings'
const detailKey = 'orderDetailsUploadSettings'
export default {
    name: 'import-orders-form',
    components: {
        FormWizard,
        TabContent,
        SimpleCsvImport,
        UploadDataSettings,
    },
    mixins: [popupMixins],
    props: [],
    data: () => {
        return {
            //headers
            orderHeaderSettings: {},
            orderHeaderFields,
            headerDataList: [],
            //details
            orderDetailsSettings: {},
            orderDetailsFields,
            detailsDataList: [],
            //generals
            i_orders: [],
            i_fields: [
                'index',
                'reference_no',
                'customer_code',
                'upload_status',
            ],
            uploading: false,
        }
    },

    filters: {
        filterStatusVariant(status) {
            switch (status) {
                case statuses.INVALID_LOCATION:
                    return 'warning'
                case statuses.ERROR:
                    return 'danger'
                case statuses.COMPLETE:
                    return 'success'
                default:
                    return 'light'
            }
        },
    },

    computed: {
        headerColumnsMap() {
            const x = { ...this.orderHeaderSettings }
            delete x.rowno
            delete x.schedule_at_date_format
            return x
        },

        detailsColumnsMap() {
            const x = { ...this.orderDetailsSettings }
            delete x.rowno
            return x
        },

        uploadProgress() {
            let uploaded = this.i_orders.filter(
                x => x.upload_status != statuses.PENDING
            ).length
            return Math.round((uploaded / this.i_orders.length) * 100)
        },
        completedUploads() {
            return this.i_orders.filter(x =>
                x.upload_status.includes('complete')
            ).length
        },
        failedUploads() {
            return this.i_orders.filter(x => x.upload_status.includes('error'))
                .length
        },
        /////////////
        configurations() {
            return this.$store.getters['company/configurations'] || []
        },
    },

    mounted() {
        this.$store.dispatch('itemType/fetchAll')
        this.$store.dispatch('orderType/fetchAll')
        this.$store.dispatch('company/fetch')

        this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
            console.log('Modal is about to be shown', bvEvent, modalId)
        })
        this.$root.$on('bv::modal::hide', () => {
            this.$emit('input', false)
        })
    },

    watch: {
        configurations: {
            handler: function(val) {
                try {
                    this.orderHeaderSettings = JSON.parse(
                        ((val || []).find(x => x.key === headerKey) || {})
                            .text_value
                    )
                } catch (err) {
                    this.orderHeaderSettings = this.orderHeaderSettings
                }
                try {
                    this.orderDetailsSettings = JSON.parse(
                        ((val || []).find(x => x.key === detailKey) || {})
                            .text_value
                    )
                } catch (err) {
                    this.orderDetailsSettings = this.orderDetailsSettings
                }
            },
            deep: true,
        },
    },

    methods: {
        /**
         * Where the value for the column index is -1, set the column setting to undefined.
         * @param data The data settings
         */
        preprocessDataSettings(data) {
            var dataCopy = { ...data }
            for (const [key, value] of Object.entries(dataCopy)) {
                dataCopy[key] = value == -1 ? undefined : value
            }
            return dataCopy
        },
        /**
         * Validates the form data based on whether it is the header data or details data.
         * @param data The form data
         * @param isheader whether the data is the header form data or not
         * @returns A list of errors if there are any or an empty list if there are none
         */
        validateDataSettings(data, isheader) {
            var errors = []
            const fields = isheader ? orderHeaderFields : orderDetailsFields
            for (const field of fields) {
                if (data[field.name] == undefined && field.required) {
                    errors.push(`${capitalCase(field.name)} is required`)
                }
            }
            return errors
        },
        /**
         * Update the data settings with the form data if the data is valid
         * @param data The form data
         * @param isheader whether the data is the header form data or not
         */
        updateDataSettings(data, isheader) {
            data = this.preprocessDataSettings(data)
            // validate data
            var res = this.validateDataSettings(data, isheader)

            // show errors if any
            if (res.length) {
                for (const err of res) {
                    this.doToast('Error', err, 'danger')
                }
                this.doToast('Error', 'Settings Update Failed', 'danger')
                // abort update due to errors
                return
            }

            const text_value = JSON.stringify(data)

            const bool_value = false
            this.$store
                .dispatch('company/updateConfiguration', {
                    key: isheader ? headerKey : detailKey,
                    text_value,
                    bool_value,
                })
                .then(() =>
                    this.doToast(
                        'Success',
                        'Settings updated successfully',
                        'success'
                    )
                )
                .catch(err => {
                    this.doToast(
                         'Error',
                       `Settings failed to update. ${err.response.data.error.message}`,
                     'danger'
                     )
                })
        },
        formatLocation({
            customer_address1: address1,
            customer_address2: address2,
            customer_city: city,
            country_code,
        }) {
            return {
                address1,
                address2,
                city,
                country_code,
            }
        },

        countryLookupSafe(progName, param) {
            try {
                return countryLookup[progName](param)
            } catch (err) {
                console.log(`safeRun- ${progName}-${param}-`, err.message)
                return null
            }
        },

        processUploadData() {
            const _this = this

            const parseNumber = currenct => {
                try {
                    return Number(currenct.replace(/[^0-9.-]+/g, ''))
                } catch (err) {
                    return 0
                }
            }
            const getCountryCode = name => {
                if (name) {
                    let countryObj = _this.countryLookupSafe('byCountry', name)
                    if (countryObj) return countryObj.iso2
                    countryObj = _this.countryLookupSafe('byIso', name)
                    if (countryObj) return countryObj.iso2
                }
                return 'JM'
            }
            if (!_this.detailsDataList.length) return false
            this.i_orders.splice(0)

            for (let _order of _this.headerDataList) {
                _order.items = _this.detailsDataList.filter(
                    y => y.reference_no === _order.reference_no
                )

                _order.items = _order.items.map(_item => {
                    delete _item.reference_no
                    delete _item.customer_code
                    _item.price = parseNumber(_item.price)
                    _item.quantity = parseNumber(_item.quantity)
                    return _item
                })
                _order.total_price = parseNumber(_order.total_price)
                _order.upload_status = statuses.PENDING
                _order.error_list = []
                _order.country_code = getCountryCode(_order.customer_country)

                // Skip the schedule field if it hasn't been set from the header file
                if (_order.schedule_at != undefined) {
                    // change date to an acceptable format for the database
                    _order.schedule_at = moment(
                        _order.schedule_at,
                        this.orderHeaderSettings.schedule_at_date_format
                    ).format('YYYY-MM-DD HH:mm:ss')
                }

                if (!_order.customer_city && _order.country_code === 'JM') {
                    _order.customer_city = 'Kingston'
                }

                this.i_orders.push(_order)
            }
            return true
        },

        ///////////////////////////

        onComplete: function() {
            this.i_orders.splice(0)
            this.$emit('cancel')
        },
        setLoading: function(value) {
            this.uploading = value
        },

        async uploadOrders() {
            // this.uploading = true
            const _this = this
            // reset all errors to pending and empty the error list
            _this.i_orders = _this.i_orders.map(x => {
                if (x.upload_status == statuses.ERROR) {
                    x.upload_status = statuses.PENDING
                    x.error_list = []
                }
                return x
            })

            const iterateUpload = async () => {
                const idx = _this.i_orders.findIndex(
                    x => x.upload_status == statuses.PENDING
                )
                //check if completed
                if (idx === -1) return true

                let nextItem = { ..._this.i_orders[idx] }
                var res = null
                // try to find the location
                try {
                    const {
                        address1,
                        address2,
                        city,
                        country_code,
                    } = _this.formatLocation(nextItem)
                    const {
                        latitude,
                        longitude,
                    } = await _this.$store.dispatch(
                        'locations/geocodeAddress',
                        { address1, address2, city, country_code }
                    )
                    nextItem = { ...nextItem, ...{ latitude, longitude } }
                    // try to upload order with location
                    res = await this.$store.dispatch(
                        'orders/uploadOrder',
                        nextItem
                    )

                    // set status to complete if the upload was successful
                    if (res.status_code == 200) {
                        nextItem.upload_status = statuses.COMPLETE
                    } else {
                        // add errors to error list and set error status if upload failed
                        nextItem.error_list = Object.entries(res.message)

                        nextItem.upload_status = statuses.ERROR
                    }
                } catch (error) {
                    console.warn(error)

                    //try to upload order without location
                    res = await this.$store.dispatch(
                        'orders/uploadOrder',
                        nextItem
                    )

                    // set status to complete with a warning if the upload was successful
                    if (res.status_code == 200) {
                        nextItem.upload_status = statuses.INVALID_LOCATION
                    } else {
                        nextItem.error_list = Object.entries(res.message)
                        nextItem.upload_status = statuses.ERROR
                    }
                } finally {
                    _this.i_orders.splice(idx, 1, nextItem)
                }

                return await iterateUpload()
            }
            return await iterateUpload()
        },
    },
}
</script>
